"use client"

import { createContext, useContext, useState, useEffect, useCallback, useRef, type ReactNode } from "react"
import { useUser, useAuth } from "@clerk/nextjs"
import type { FurnitureItem } from "../actions"
import { captureError } from "@/lib/posthog-error-utils"

const STORAGE_KEY = "loop-proto-chat-favourites"
const API_URL = process.env.NEXT_PUBLIC_API_URL

// Check if API is configured
const isApiConfigured = (): boolean => {
  if (!API_URL) {
    console.warn("NEXT_PUBLIC_API_URL not configured - favourites will only be stored locally")
    return false
  }
  return true
}

// localStorage structure with userId tracking
interface StoredFavourites {
  userId: string | null  // Clerk user ID, null for anonymous
  favourites: FurnitureItem[]
}

// API response structure from backend
interface FavouriteApiResponse {
  id: number
  listing_type: string
  listing_id: string
  item_data: FurnitureItem
  created_at: string
}

// Generate unique ID for a furniture item
function getItemId(item: FurnitureItem): string {
  if (item.listing_type === "kivo") {
    return `kivo-${item.kivo_id || item.kivo_uuid}`
  }
  return `ebay-${item.ebay_id || item.ebay_uuid}`
}

// Get listing_id for API calls (without the prefix)
function getListingId(item: FurnitureItem): string {
  if (item.listing_type === "kivo") {
    return String(item.kivo_id || item.kivo_uuid || "")
  }
  return String(item.ebay_id || item.ebay_uuid || "")
}

interface FavouritesContextType {
  favourites: FurnitureItem[]
  addFavourite: (item: FurnitureItem) => void
  removeFavourite: (item: FurnitureItem) => void
  toggleFavourite: (item: FurnitureItem) => void
  isFavourite: (item: FurnitureItem) => boolean
  getFavourites: () => FurnitureItem[]
  clearFavourites: () => void
  isInitialized: boolean
  isSyncing: boolean
  isAuthenticated: boolean
}

const FavouritesContext = createContext<FavouritesContextType | undefined>(undefined)

export function FavouritesProvider({ children }: { children: ReactNode }) {
  const { user, isLoaded: isUserLoaded, isSignedIn } = useUser()
  const { getToken } = useAuth()
  const [favourites, setFavourites] = useState<FurnitureItem[]>([])
  const [isInitialized, setIsInitialized] = useState(false)
  const [isSyncing, setIsSyncing] = useState(false)
  const previousIsSignedIn = useRef<boolean | undefined>(undefined)
  const hasSyncedRef = useRef(false)

  // Helper to save to localStorage with userId
  const saveToLocalStorage = useCallback((items: FurnitureItem[], userId: string | null) => {
    try {
      const data: StoredFavourites = { userId, favourites: items }
      localStorage.setItem(STORAGE_KEY, JSON.stringify(data))
    } catch (error) {
      console.error("Error saving favourites:", error)
      captureError(error, { operation: 'saveFavourites', component: 'favourites-context' })
    }
  }, [])

  // Helper to load from localStorage
  const loadFromLocalStorage = useCallback((): StoredFavourites | null => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY)
      if (stored) {
        const parsed = JSON.parse(stored)
        // Handle legacy format (array only)
        if (Array.isArray(parsed)) {
          return { userId: null, favourites: parsed }
        }
        return parsed as StoredFavourites
      }
    } catch (error) {
      console.error("Error loading favourites:", error)
      captureError(error, { operation: 'loadFavourites', component: 'favourites-context' })
    }
    return null
  }, [])

  // API helper with auth
  const fetchWithAuth = useCallback(async (endpoint: string, options: RequestInit = {}) => {
    if (!isApiConfigured()) {
      throw new Error("API not configured")
    }
    const token = await getToken()
    if (!token) throw new Error("Not authenticated")
    
    return fetch(`${API_URL}${endpoint}`, {
      ...options,
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
        ...options.headers,
      },
    })
  }, [getToken])

  // Fetch favourites from server
  const fetchServerFavourites = useCallback(async (): Promise<FurnitureItem[]> => {
    try {
      const response = await fetchWithAuth("/api/favourites/")
      if (!response.ok) {
        // Try to get error details from response
        let errorDetail = ''
        try {
          const errorData = await response.json()
          errorDetail = errorData.detail || errorData.error || JSON.stringify(errorData)
        } catch {
          errorDetail = await response.text().catch(() => '')
        }
        console.error(`Favourites API error ${response.status}:`, errorDetail)
        // Don't throw for auth errors - just return empty (user might not be synced yet)
        if (response.status === 401 || response.status === 403) {
          console.warn("User may not be synced to backend yet - favourites will sync on next login")
          return []
        }
        throw new Error(`Failed to fetch favourites: ${response.status} - ${errorDetail}`)
      }
      const data: FavouriteApiResponse[] = await response.json()
      // Server returns array of { item_data, ... } - extract the item_data
      return data.map((fav) => fav.item_data)
    } catch (error) {
      console.error("Error fetching server favourites:", error)
      captureError(error, { operation: 'fetchServerFavourites', component: 'favourites-context' })
      return []
    }
  }, [fetchWithAuth])

  // Sync favourites to server (bulk)
  const syncToServer = useCallback(async (items: FurnitureItem[]) => {
    if (!isApiConfigured()) return null
    try {
      const response = await fetchWithAuth("/api/favourites/sync/", {
        method: "POST",
        body: JSON.stringify({ favourites: items }),
      })
      if (!response.ok) {
        // Gracefully handle auth errors
        if (response.status === 401 || response.status === 403) {
          console.warn("Cannot sync favourites - user may not be synced to backend yet")
          return null
        }
        throw new Error(`Failed to sync favourites: ${response.status}`)
      }
      const data: { synced_count: number; favourites: FavouriteApiResponse[] } = await response.json()
      // Return the server's authoritative list
      return data.favourites.map((fav) => fav.item_data)
    } catch (error) {
      console.error("Error syncing to server:", error)
      captureError(error, { operation: 'syncToServer', component: 'favourites-context' })
      return null
    }
  }, [fetchWithAuth])

  // Add favourite to server (fire and forget - don't block UI)
  const addToServer = useCallback(async (item: FurnitureItem) => {
    if (!isApiConfigured()) return
    try {
      const response = await fetchWithAuth("/api/favourites/", {
        method: "POST",
        body: JSON.stringify({ item_data: item }),
      })
      if (!response.ok && response.status !== 401 && response.status !== 403) {
        console.error(`Failed to add favourite to server: ${response.status}`)
        captureError(new Error(`Server returned ${response.status}`), { 
          operation: 'addToServer', 
          component: 'favourites-context',
          status: response.status 
        })
      }
    } catch (error) {
      // Silently fail - local storage will have the data
      console.error("Error adding favourite to server:", error)
      captureError(error, { operation: 'addToServer', component: 'favourites-context' })
    }
  }, [fetchWithAuth])

  // Remove favourite from server (fire and forget - don't block UI)
  const removeFromServer = useCallback(async (item: FurnitureItem) => {
    if (!isApiConfigured()) return
    try {
      const listingId = getListingId(item)
      const response = await fetchWithAuth(`/api/favourites/${item.listing_type}/${listingId}/`, {
        method: "DELETE",
      })
      if (!response.ok && response.status !== 404 && response.status !== 401 && response.status !== 403) {
        console.error(`Failed to remove favourite from server: ${response.status}`)
        captureError(new Error(`Server returned ${response.status}`), { 
          operation: 'removeFromServer', 
          component: 'favourites-context',
          status: response.status 
        })
      }
    } catch (error) {
      // Silently fail - local storage will have the updated data
      console.error("Error removing favourite from server:", error)
      captureError(error, { operation: 'removeFromServer', component: 'favourites-context' })
    }
  }, [fetchWithAuth])

  // Clear all favourites from server
  const clearServerFavourites = useCallback(async () => {
    if (!isApiConfigured() || !isSignedIn) return
    try {
      // Fetch all favourites and delete them one by one
      // Note: Backend doesn't have a bulk delete endpoint, so we delete individually
      const serverFavs = await fetchServerFavourites()
      await Promise.allSettled(
        serverFavs.map(item => removeFromServer(item))
      )
    } catch (error) {
      // Silently fail - local storage is already cleared
      console.error("Error clearing server favourites:", error)
      captureError(error, { operation: 'clearServerFavourites', component: 'favourites-context' })
    }
  }, [isSignedIn, fetchServerFavourites, removeFromServer])

  // Initial load and auth state handling
  useEffect(() => {
    if (!isUserLoaded) return

    const handleAuthStateChange = async () => {
      const currentUserId = user?.id || null
      const stored = loadFromLocalStorage()
      const wasSignedIn = previousIsSignedIn.current
      
      // Detect logout
      if (wasSignedIn === true && !isSignedIn) {
        // User logged out - clear localStorage
        localStorage.removeItem(STORAGE_KEY)
        setFavourites([])
        setIsInitialized(true)
        previousIsSignedIn.current = false
        hasSyncedRef.current = false
        return
      }

      // Not signed in - use local favourites if they belong to anonymous user
      if (!isSignedIn) {
        if (stored && stored.userId === null) {
          setFavourites(stored.favourites)
        } else if (stored && stored.userId !== null) {
          // Different user's data - clear it
          localStorage.removeItem(STORAGE_KEY)
          setFavourites([])
        } else {
          setFavourites([])
        }
        setIsInitialized(true)
        previousIsSignedIn.current = false
        return
      }

      // User is signed in
      previousIsSignedIn.current = true

      // Skip sync if we already synced this session
      if (hasSyncedRef.current && stored?.userId === currentUserId) {
        setFavourites(stored.favourites)
        setIsInitialized(true)
        return
      }

      setIsSyncing(true)

      try {
        // Fetch server favourites
        const serverFavs = await fetchServerFavourites()

        // Determine what local favourites to merge
        let localFavsToMerge: FurnitureItem[] = []
        if (stored) {
          if (stored.userId === null || stored.userId === currentUserId) {
            // Anonymous data or same user's data - merge it
            localFavsToMerge = stored.favourites
          }
          // Different user's data - don't merge, just use server data
        }

        // Merge: server takes precedence, add unique locals
        const merged = [...serverFavs]
        localFavsToMerge.forEach(local => {
          if (!merged.some(s => getItemId(s) === getItemId(local))) {
            merged.push(local)
          }
        })

        // Sync merged list to server if we added any local items
        if (localFavsToMerge.length > 0 && merged.length > serverFavs.length) {
          const syncedResult = await syncToServer(merged)
          if (syncedResult) {
            setFavourites(syncedResult)
            saveToLocalStorage(syncedResult, currentUserId)
          } else {
            setFavourites(merged)
            saveToLocalStorage(merged, currentUserId)
          }
        } else {
          setFavourites(merged)
          saveToLocalStorage(merged, currentUserId)
        }

        hasSyncedRef.current = true
      } catch (error) {
        console.error("Error during auth state sync:", error)
        captureError(error, { operation: 'authStateSync', component: 'favourites-context' })
        // Fallback to local data
        if (stored?.userId === currentUserId) {
          setFavourites(stored.favourites)
        } else {
          setFavourites([])
        }
      } finally {
        setIsSyncing(false)
        setIsInitialized(true)
      }
    }

    handleAuthStateChange()
  }, [isUserLoaded, isSignedIn, user?.id, loadFromLocalStorage, saveToLocalStorage, fetchServerFavourites, syncToServer])

  // Sync across tabs (only for same user)
  useEffect(() => {
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === STORAGE_KEY && e.newValue) {
        try {
          const parsed = JSON.parse(e.newValue) as StoredFavourites
          const currentUserId = user?.id || null
          // Only sync if it's for the same user
          if (parsed.userId === currentUserId) {
            setFavourites(parsed.favourites)
          }
        } catch (error) {
          console.error("Error syncing favourites:", error)
          captureError(error, { operation: 'tabSyncFavourites', component: 'favourites-context' })
        }
      }
    }

    window.addEventListener("storage", handleStorageChange)
    return () => window.removeEventListener("storage", handleStorageChange)
  }, [user?.id])

  const addFavourite = useCallback((item: FurnitureItem) => {
    setFavourites((prev) => {
      const itemId = getItemId(item)
      const exists = prev.some((fav) => getItemId(fav) === itemId)
      if (exists) return prev
      
      const newFavs = [...prev, item]
      const currentUserId = user?.id || null
      saveToLocalStorage(newFavs, currentUserId)
      
      // Sync to server if authenticated
      if (isSignedIn) {
        addToServer(item)
      }
      
      return newFavs
    })
  }, [isSignedIn, user?.id, saveToLocalStorage, addToServer])

  const removeFavourite = useCallback((item: FurnitureItem) => {
    setFavourites((prev) => {
      const itemId = getItemId(item)
      const newFavs = prev.filter((fav) => getItemId(fav) !== itemId)
      const currentUserId = user?.id || null
      saveToLocalStorage(newFavs, currentUserId)
      
      // Sync to server if authenticated
      if (isSignedIn) {
        removeFromServer(item)
      }
      
      return newFavs
    })
  }, [isSignedIn, user?.id, saveToLocalStorage, removeFromServer])

  const toggleFavourite = useCallback((item: FurnitureItem) => {
    setFavourites((prev) => {
      const itemId = getItemId(item)
      const exists = prev.some((fav) => getItemId(fav) === itemId)
      
      if (exists) {
        // Remove favourite
        const newFavs = prev.filter((fav) => getItemId(fav) !== itemId)
        const currentUserId = user?.id || null
        saveToLocalStorage(newFavs, currentUserId)
        
        // Sync to server if authenticated
        if (isSignedIn) {
          removeFromServer(item)
        }
        
        return newFavs
      } else {
        // Add favourite
        const newFavs = [...prev, item]
        const currentUserId = user?.id || null
        saveToLocalStorage(newFavs, currentUserId)
        
        // Sync to server if authenticated
        if (isSignedIn) {
          addToServer(item)
        }
        
        return newFavs
      }
    })
  }, [isSignedIn, user?.id, saveToLocalStorage, addToServer, removeFromServer])

  const isFavourite = useCallback(
    (item: FurnitureItem) => {
      const itemId = getItemId(item)
      return favourites.some((fav) => getItemId(fav) === itemId)
    },
    [favourites]
  )

  const getFavourites = useCallback(() => {
    return favourites
  }, [favourites])

  const clearFavourites = useCallback(() => {
    setFavourites([])
    const currentUserId = user?.id || null
    saveToLocalStorage([], currentUserId)
    // Clear server favourites if authenticated
    if (isSignedIn) {
      clearServerFavourites()
    }
  }, [isSignedIn, user?.id, saveToLocalStorage, clearServerFavourites])

  return (
    <FavouritesContext.Provider
      value={{
        favourites,
        addFavourite,
        removeFavourite,
        toggleFavourite,
        isFavourite,
        getFavourites,
        clearFavourites,
        isInitialized,
        isSyncing,
        isAuthenticated: isSignedIn ?? false,
      }}
    >
      {children}
    </FavouritesContext.Provider>
  )
}

export function useFavourites() {
  const context = useContext(FavouritesContext)
  if (context === undefined) {
    throw new Error("useFavourites must be used within a FavouritesProvider")
  }
  return context
}
